import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { IPermissions } from '../../types';
import { fetchPermissions as fetchPermissionsApiCall } from '../../utils/api';
import type { RootState } from '../store';

export const fetchPermissions = createAsyncThunk(
  'permissions/fetchStatus',
  async () => {
    const p: string[] = await fetchPermissionsApiCall();

    const permissions: IPermissions = {
      pbDataflowEnabled: p.includes('powerBiDataflow'),
      getPBReportsEnabled: p.includes('getPowerBiReports'),
      getPBSharedReportsEnabled: p.includes('powerBiGetSharedReports'),
      updatePBReportEnabled: p.includes('powerBiEditReportMetadataAsync'),
      getTReportsEnabled: p.includes('getTableauReports'),
      getTSharedReportsEnabled: p.includes('tableauGetSharedReports'),
      updateTReportEnabled: p.includes('tableauEditReportMetadataAsync'),
      pbReportEmbedEnabled: p.includes('generatePowerBiReportEmbedDetails'),
      tReportEmbedEnabled: p.includes('generateTableauViewEmbedDetails'),
      sharePBReportEnabled: p.includes('sharePowerBiReport'),
      unsharePBReportEnabled: p.includes('unsharePowerBiReport'),
      shareTReportEnabled: p.includes('shareTableauWorkbook'),
      unshareTReportEnabled: p.includes('deleteSharedTableauWorkbook'),
      downloadPbidsFileEnabled: p.includes('downloadPbidsFile'),
      updateSettingsEnabled: p.includes('updateSettings'),
    };

    return permissions;
  }
);

export interface IPermissionsState {
  permissions: IPermissions | null;
  loading?: boolean;
  saving?: boolean;
  failed?: boolean;
}

const initialState: IPermissionsState = {
  permissions: null,
  loading: true,
  saving: false,
  failed: false,
};

export const permissionsSlice = createSlice({
  name: 'permissions',
  initialState,
  reducers: {
    setPermissions: (state, action: PayloadAction<IPermissions | null>) => {
      state.permissions = action.payload;
    },
    clearPermissions: (state) => {
      state.permissions = null;
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(fetchPermissions.fulfilled, (state, action) => {
        state.permissions = action.payload;
        state.loading = false;
        state.failed = false;
      })
      .addCase(fetchPermissions.pending, (state) => {
        state.permissions = null;
        state.loading = true;
        state.failed = false;
      })
      .addCase(fetchPermissions.rejected, (state) => {
        state.permissions = null;
        state.loading = false;
        state.failed = true;
      });
  },
});

export const { setPermissions, clearPermissions } = permissionsSlice.actions;
export const selectPermissions = (state: RootState) => state.permissions;

export default permissionsSlice.reducer;
